import { FC, useMemo, useState } from "react";

import { Box, Skeleton, Stack, useTheme } from "@mui/material";

import { formatCurrency } from "../../helpers/formatters";

import { trpc } from "@/common/helpers/trpc";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

interface PastRecipientsProps {
  funderId: string;
  title?: string;
  description?: string;
}

interface RecipientItem {
  RECIPIENT_NAME: string | null;
  RECIPIENT_CITY: string | null;
  RECIPIENT_STATE: string | null;
  AMOUNT: number | null;
  GRANT_YEAR: number | null;
  EIN: string | null;
  N_YEARS_RECIPIENT_FOR_FUNDER: number;
}

const INITIAL_VISIBLE = 9;
const FETCH_LIMIT = 50;

const titleCase = (value: string): string => value.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) {
    return "?";
  }
  if (words.length === 1) {
    return words[0]!.slice(0, 2).toUpperCase();
  }
  return `${words[0]![0] ?? ""}${words[1]![0] ?? ""}`.toUpperCase();
};

const getLocation = (city: string | null, state: string | null): string => {
  if (city && state) {
    return `${titleCase(city)}, ${state.toUpperCase()}`;
  }
  if (city) {
    return titleCase(city);
  }
  if (state) {
    return state.toUpperCase();
  }
  return "";
};

const CalendarIcon: FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 16, height: 16, flexShrink: 0 }}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </Box>
);

const MapPinIcon: FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 13, height: 13, flexShrink: 0 }}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </Box>
);

interface RecipientRowProps {
  item: RecipientItem;
  isFirst: boolean;
}

const RecipientRow: FC<RecipientRowProps> = ({ item, isFirst }) => {
  const theme = useTheme();
  const name = item.RECIPIENT_NAME ? titleCase(item.RECIPIENT_NAME) : "Unknown recipient";
  const location = getLocation(item.RECIPIENT_CITY, item.RECIPIENT_STATE);
  const isMultiYear = item.N_YEARS_RECIPIENT_FOR_FUNDER > 1;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        px: 2,
        py: 1.5,
        ...(isFirst ? {} : { borderTop: `1px solid ${theme.palette.divider}` }),
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1.5,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "rgba(85,85,231,0.10)",
          color: "#3F3FA8",
        }}
      >
        <DashboardTypography variant="caption" sx={{ fontWeight: 600 }}>
          {getInitials(name)}
        </DashboardTypography>
      </Box>

      <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
        <DashboardTypography
          variant="subtitle2"
          vibe="text-neutral-intense"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {name}
        </DashboardTypography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          {location ? (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              sx={{ color: theme.palette.text.secondary, minWidth: 0 }}
            >
              <MapPinIcon />
              <DashboardTypography
                variant="caption"
                vibe="text-neutral-quiet"
                sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {location}
              </DashboardTypography>
            </Stack>
          ) : null}
          {isMultiYear ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                px: 0.75,
                py: 0.25,
                borderRadius: 999,
                flexShrink: 0,
                backgroundColor: "rgba(85,85,231,0.10)",
              }}
            >
              <DashboardTypography variant="caption" sx={{ color: "#3F3FA8", fontWeight: 600, whiteSpace: "nowrap" }}>
                Multi-year recipient
              </DashboardTypography>
            </Box>
          ) : null}
        </Stack>
      </Stack>

      <DashboardTypography variant="subtitle2" vibe="text-neutral-intense" sx={{ flexShrink: 0, fontWeight: 700 }}>
        {formatCurrency(item.AMOUNT)}
      </DashboardTypography>
    </Stack>
  );
};

export const PastRecipients: FC<PastRecipientsProps> = ({
  funderId,
  title = "Past recipients",
  description = "Organizations this funder has supported in recent years — useful for gauging fit and typical award size.",
}) => {
  const theme = useTheme();
  const [showAll, setShowAll] = useState(false);

  const { data, isLoading } = trpc.grantFinder_listPastRecipients.useQuery({ funderId, limit: FETCH_LIMIT });

  const items: RecipientItem[] = data?.items ?? [];
  const total = data?.total ?? 0;

  const yearCountLabel = useMemo(() => {
    const map = new Map<number, number>();
    for (const yearCount of data?.yearCounts ?? []) {
      if (yearCount.GRANT_YEAR !== null) {
        map.set(yearCount.GRANT_YEAR, yearCount.COUNT);
      }
    }
    return map;
  }, [data?.yearCounts]);

  const visibleItems = showAll ? items : items.slice(0, INITIAL_VISIBLE);
  const showMoreLabel =
    total > items.length
      ? `Show ${items.length.toLocaleString()} loaded recipients ⌄`
      : `Show all ${total.toLocaleString()} recipients ⌄`;

  const groupedByYear = useMemo(() => {
    const groups: { year: number | null; rows: RecipientItem[] }[] = [];
    for (const item of visibleItems) {
      const last = groups[groups.length - 1];
      if (last && last.year === item.GRANT_YEAR) {
        last.rows.push(item);
      } else {
        groups.push({ year: item.GRANT_YEAR, rows: [item] });
      }
    }
    return groups;
  }, [visibleItems]);

  if (isLoading) {
    return (
      <Stack spacing={1.5} sx={{ pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
        <Skeleton variant="text" width={180} height={28} />
        <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
      </Stack>
    );
  }

  if (total === 0 || items.length === 0) {
    return null;
  }

  const hasMore = !showAll && items.length > INITIAL_VISIBLE;

  return (
    <Stack spacing={2} sx={{ pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
        <DashboardTypography variant="h5" vibe="text-neutral-moderate">
          {title}
        </DashboardTypography>
        <DashboardTypography variant="caption" vibe="text-neutral-quiet">
          {`${total.toLocaleString()} total recipients`}
        </DashboardTypography>
      </Stack>

      <DashboardTypography variant="body2" vibe="text-neutral-quiet">
        {description}
      </DashboardTypography>

      <Stack spacing={3} sx={{ pt: 0.5 }}>
        {groupedByYear.map((group, groupIndex) => {
          const yearCount = group.year !== null ? yearCountLabel.get(group.year) : undefined;
          const countText =
            yearCount !== undefined
              ? yearCount > 30
                ? "30+ recipients"
                : `${yearCount} recipient${yearCount === 1 ? "" : "s"}`
              : null;

          return (
            <Stack key={`${group.year ?? "unknown"}-${groupIndex}`} spacing={1.25}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ color: theme.palette.text.secondary }}>
                <CalendarIcon />
                <DashboardTypography variant="subtitle2" vibe="text-neutral-intense">
                  {group.year ?? "—"}
                </DashboardTypography>
                {countText ? (
                  <DashboardTypography variant="caption" vibe="text-neutral-quiet">
                    {countText}
                  </DashboardTypography>
                ) : null}
              </Stack>

              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  backgroundColor: theme.palette.common.white,
                  overflow: "hidden",
                }}
              >
                {group.rows.map((row, rowIndex) => (
                  <RecipientRow
                    key={`${row.RECIPIENT_NAME ?? "row"}-${rowIndex}`}
                    item={row}
                    isFirst={rowIndex === 0}
                  />
                ))}
              </Box>
            </Stack>
          );
        })}
      </Stack>

      {hasMore ? (
        <Box>
          <DashboardButton variant="outlined" vibe="neutral" size="medium" onClick={() => setShowAll(true)}>
            {showMoreLabel}
          </DashboardButton>
        </Box>
      ) : null}
    </Stack>
  );
};
